import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Button } from 'antd';
import {
    DashboardOutlined,
    CarOutlined,
    CalendarOutlined,
    UserOutlined,
    BellOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    SettingOutlined,
    LogoutOutlined,
    HistoryOutlined,
    BarChartOutlined,
    DollarOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalOutlined } from '@ant-design/icons';
import { Dropdown, Drawer } from 'antd';

const { Sider, Content } = Layout;

const SidebarLayout = ({ children }) => {
    const { t, i18n } = useTranslation();
    const [collapsed, setCollapsed] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Get user role from localStorage
        const role = localStorage.getItem('userRole');
        setUserRole(role || '');

        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Automatically close the drawer when a route/location change occurs
    useEffect(() => {
        setDrawerVisible(false);
    }, [location]);

    const toggleCollapsed = () => setCollapsed(!collapsed);

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        document.body.dir = lng === 'ar' ? 'rtl' : 'ltr';
    };

    const languageMenu = {
        items: [
            { key: 'fr', label: 'Français', onClick: () => changeLanguage('fr') },
            { key: 'ar', label: 'العربية', onClick: () => changeLanguage('ar') },
            { key: 'en', label: 'English', onClick: () => changeLanguage('en') },
        ]
    };

    // Common menu items for all users
    const commonMenuItems = [
        { key: '/dashboard', icon: <DashboardOutlined />, label: t('dashboard') },
        { key: '/voitures', icon: <CarOutlined />, label: t('vehicles') },
        { key: '/clients', icon: <UserOutlined />, label: t('clients') },
        { key: '/reservations', icon: <CalendarOutlined />, label: t('reservations') },
        { key: '/location', icon: <CarOutlined />, label: t('rental') },
        { key: '/historique', icon: <HistoryOutlined />, label: t('history') },
        { key: '/entretien', icon: <CarOutlined />, label: t('maintenance') },
        { key: '/calendar', icon: <CalendarOutlined />, label: t('calendar') },
    ];

    // Admin-only menu items
    const adminMenuItems = [
        { key: '/finance', icon: <DollarOutlined />, label: t('finance') },
        { key: '/CarTransactions', icon: <BarChartOutlined />, label: t('vehicle_stats') },
        { key: '/statistics', icon: <BarChartOutlined />, label: t('general_stats') },
        {
            key: '/account',
            icon: <SettingOutlined />,
            label: t('account'),
        },
    ];

    // Combine menu items based on user role
    const menuItems = [
        ...commonMenuItems,
        ...(userRole === 'admin' ? adminMenuItems : [])
    ];

    const bottomMenuItems = [
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: t('logout'),
            danger: true,
        }
    ];

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/');
    };

    const renderSidebarContent = (isDrawer = false) => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            background: '#1E293B'
        }}>
            {/* Header Section - Fixed */}
            <div style={{
                padding: '16px',
                textAlign: 'center',
                borderBottom: '1px solid #334155',
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                <Avatar size={40} src="/logo.png" style={{ backgroundColor: '#3B82F6' }} />
                {(!collapsed || isDrawer) && (
                    <span style={{
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        marginLeft: i18n.language === 'ar' ? '0' : '10px',
                        marginRight: i18n.language === 'ar' ? '10px' : '0'
                    }}>
                        Dominosoft
                    </span>
                )}
            </div>

            {/* Role & Language Indicator - Fixed */}
            <div style={{
                padding: '8px 16px',
                color: '#94A3B8',
                fontSize: '12px',
                textTransform: 'uppercase',
                borderBottom: '1px solid #334155',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                {(!collapsed || isDrawer) && (
                    <span>
                        {userRole === 'admin' ? t('admin') : t('employee')}
                    </span>
                )}
                <Dropdown menu={languageMenu} placement="bottomRight">
                    <Button
                        type="text"
                        size="small"
                        icon={<GlobalOutlined />}
                        style={{ color: '#94A3B8', padding: 0 }}
                    />
                </Dropdown>
            </div>

            {/* Scrollable Menu Section */}
            <div
                className="sidebar-menu"
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#475569 transparent',
                    minHeight: 0,
                }}
            >
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    onSelect={({ key }) => navigate(key)}
                    style={{
                        background: '#1E293B',
                        border: 'none',
                    }}
                />
            </div>

            {/* Fixed Bottom Section */}
            <div style={{
                background: '#1E293B',
                flexShrink: 0,
            }}>
                {/* Logout Menu */}
                <div style={{
                    borderTop: '1px solid #334155',
                    borderBottom: !isDrawer ? '1px solid #334155' : 'none',
                }}>
                    <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        items={bottomMenuItems}
                        onSelect={({ key }) => {
                            if (key === 'logout') {
                                handleLogout();
                            }
                        }}
                        style={{
                            background: '#1E293B',
                            border: 'none',
                        }}
                    />
                </div>

                {/* Collapse Button - Sider Only */}
                {!isDrawer && (
                    <div style={{
                        padding: '16px',
                        textAlign: 'center',
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={toggleCollapsed}
                            style={{
                                color: 'white',
                                fontSize: '16px',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <Layout style={{ minHeight: '100vh', flexDirection: 'column' }}>
                {/* Mobile Top Navbar */}
                <div style={{
                    background: '#1E293B',
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 40,
                    borderBottom: '1px solid #334155',
                    flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row' }}>
                        <Button
                            type="text"
                            icon={<MenuUnfoldOutlined style={{ color: 'white', fontSize: 20 }} />}
                            onClick={() => setDrawerVisible(true)}
                            style={{
                                marginLeft: i18n.language === 'ar' ? 8 : 0,
                                marginRight: i18n.language === 'ar' ? 0 : 8
                            }}
                        />
                        <Avatar size={36} src="/logo.png" style={{ backgroundColor: '#3B82F6' }} />
                        <span style={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            marginLeft: i18n.language === 'ar' ? 0 : 8,
                            marginRight: i18n.language === 'ar' ? 8 : 0
                        }}>
                            Dominosoft
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row' }}>
                        <span style={{ color: '#94A3B8', fontSize: '11px', textTransform: 'uppercase' }}>
                            {userRole === 'admin' ? t('admin') : t('employee')}
                        </span>
                        <Dropdown menu={languageMenu} placement="bottomRight">
                            <Button
                                type="text"
                                size="small"
                                icon={<GlobalOutlined />}
                                style={{ color: '#94A3B8', padding: 0 }}
                            />
                        </Dropdown>
                    </div>
                </div>

                <Drawer
                    placement={i18n.language === 'ar' ? 'right' : 'left'}
                    onClose={() => setDrawerVisible(false)}
                    open={drawerVisible}
                    width={250}
                    closable={false}
                    styles={{ body: { padding: 0, background: '#1E293B' } }}
                    style={{ background: '#1E293B' }}
                >
                    {renderSidebarContent(true)}
                </Drawer>

                <Content style={{
                    padding: '12px',
                    background: '#F8FAFC',
                    minHeight: 'calc(100vh - 64px)',
                    marginTop: 64,
                    direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                    overflow: 'auto'
                }}>
                    <div style={{ minHeight: '100%' }}>
                        {children}
                    </div>
                </Content>
            </Layout>
        );
    }

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                collapsible
                collapsed={collapsed}
                trigger={null}
                width={250}
                theme="dark"
                style={{
                    background: '#1E293B',
                    position: 'fixed',
                    height: '100vh',
                    left: i18n.language === 'ar' ? 'auto' : 0,
                    right: i18n.language === 'ar' ? 0 : 'auto',
                    top: 0,
                    zIndex: 1000,
                    overflow: 'hidden',
                }}
            >
                {renderSidebarContent(false)}
            </Sider>

            {/* Main Content Area */}
            <Content style={{
                marginLeft: i18n.language === 'ar' ? 0 : (collapsed ? 80 : 250),
                marginRight: i18n.language === 'ar' ? (collapsed ? 80 : 250) : 0,
                padding: '20px',
                background: '#F8FAFC',
                minHeight: '100vh',
                transition: 'margin 0.2s',
                overflow: 'auto',
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
            }}>
                <div style={{
                    minHeight: '100vh',
                }}>
                    {children}
                </div>
            </Content>
        </Layout>
    );
};

export default SidebarLayout;